import _ from 'lodash';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Badge, HStack, Heading, SimpleGrid, Text, VStack,
} from '@chakra-ui/react';
import {
  IoPeopleOutline,
  IoTimeOutline,
  IoCheckmarkCircleOutline,
  IoIdCardOutline,
  IoCloseCircleOutline,
} from "react-icons/io5";
import { checkServiceHasChildren, formatTime } from '../../../../store/helpers';
import PropTypes from 'prop-types';

import { TICKET_STATUS } from '../../../../store/constants';
import { updateSelectedTicketsStatus } from '../../../../store/actions/realtimeAction';
import StatsCard from '../../../StatsCard';
import CustomerCard from '../../../CustomerCard';
import SelectBranchInfo from '../../../misc/SelectBranchInfo';
import useTickets from '../../../../hooks/reports/branches/realtime/useTickets';
import useAgents from '../../../../hooks/reports/branches/realtime/useAgents';
import useServices from '../../../../hooks/reports/branches/realtime/useServices';
import RealtimeEnterpriseSummary, {
  formatSummaryNumber,
  sumSummaryRows,
} from '../../../reports/RealtimeEnterpriseSummary';

function CustomersInQueue({ branches }) {
  CustomersInQueue.propTypes = {
    branches: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  };

  CustomersInQueue.defaultProps = {
    branches: [],
  };

  const {
    servicesSubscriptions,
    services,
    users,
    organizationId,
    workDay,
    branchId
  } = useSelector((state) => state.defaultReducer);

  const {
    tickets,
    customers,
    selectedTicketsStatus,
    timeNow
  } = useSelector((state) => state.realtimeReducer);
  const { numCompletedTickets, numActiveTickets, numNoShowTickets } = useTickets({ branchId });
  const { numOnline, numAgents} = useAgents({ branchId });

  const { iamRoles } = useSelector((state) => state.permissionsReducer);

  const dispatch = useDispatch();
  const [expandedAll, setExpandedAll] = useState([0, 0, 0, 0]);
  const [activeTab, setActiveTab] = useState('customers');

  // Reset ticket filter on mount so dashboard always shows all tickets
  useEffect(() => {
    dispatch(updateSelectedTicketsStatus(''));
  }, [dispatch]);
  const agentRoleId = iamRoles.find((a) => a.name === 'agent')?.id;
  const { agentsData } = useAgents({ branchId });
  const { servicesData } = useServices({ branchId });

  const agents = _.cloneDeep(users).filter(
    (a) => Number(a.agentStatus) === 1 || a.roleId === agentRoleId,
  );

  const ticketsClone = _.cloneDeep(tickets)
    .filter((a) => a.organizationId === organizationId)
    .filter(
      (a) => !selectedTicketsStatus.length
        || (selectedTicketsStatus.length
          && Number(a.status) === Number(selectedTicketsStatus)),
    );
  const customersClone = _.cloneDeep(customers).filter(
    (a) => a.organizationId === organizationId,
  );
  const todayCustomers = customersClone.filter((a) => dayjs.unix(a.updated).isSame(dayjs(), 'day'));

  const servicesSubscriptionsClone = _.cloneDeep(servicesSubscriptions);
  const servicesClone = _.cloneDeep(services);

  const branchesMap = branches.reduce((acc, { id: branchId }) => {
    acc[branchId] = todayCustomers.filter((a) => a.branchId === branchId);
    return acc;
  }, {});
  const branchesData = Object.keys(branchesMap).map((branchId) => {
    let branchServices = servicesSubscriptionsClone.find(
      (service) => service.branchId === branchId,
    )?.services;

    branchServices = checkServiceHasChildren(servicesClone)
      .filter(
        (service) => branchServices?.includes(service.id) && !service.hasChildren,
      )
      .map((service) => service.id);
    const branchName = branches.find((branch) => branch.id === branchId)?.name
      || `Branch-${branchId}`;
    const customersData = ticketsClone
      .filter((a) => a.branchId === branchId)
      .map((ticket) => {
        const customer = todayCustomers.find(
          (a) => a.mobileNumber === ticket.mobileNumber,
        );
        const agent = agents.find((a) => a.id === ticket?.manage?.userId);
        const agentFullName = agent
          ? `${agent?.name} ${agent?.lastname}`
          : 'Unnamed Agent';
        const waitingTime = Number(ticket.status) < 1
          ? dayjs().unix() - ticket.created
          : ticket.sessionStart - ticket.created;
        const serviceName = servicesClone.find(
          (a) => a.id === ticket.serviceId,
        )?.name;
        let totalTimeInBranch = null;
        if (ticket.status === TICKET_STATUS.COMPLETED) {
          totalTimeInBranch = Math.abs(
            dayjs
              .unix(ticket.sessionStart)
              .diff(dayjs.unix(ticket.created), 'seconds'),
          ) + ticket.sessionInSeconds;
        } else if (
          ticket.status === TICKET_STATUS.ACTIVE
          || ticket.status === TICKET_STATUS.IN_QUEUE
        ) {
          totalTimeInBranch = timeNow - ticket.created;
        }

        return {
          customer,
          waitingTime,
          serviceName,
          agentFullName,
          ticket,
          totalTimeInBranch,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.ticket.status - b.ticket.status);
    const numQueued = customersData.filter(
      (a) => Number(a.ticket.status) === TICKET_STATUS.IN_QUEUE,
    ).length;
    const maxQueueTimeNow = Math.max(
      ...customersData
        .filter((a) => Number(a.ticket.status) === TICKET_STATUS.IN_QUEUE)
        .map((a) => dayjs().diff(dayjs.unix(a.ticket.created), 'seconds')),
    );

    return {
      branchName,
      branchServices,
      branchId,
      numCustomers:
        ticketsClone.filter((a) => a.branchId === branchId)?.length || 0,
      customersData,
      numQueued,
      maxQueueTimeNow,
    };
  });
  const enterpriseSummaryRows = branchesData.map(
    ({ branchName, numCustomers, numQueued, maxQueueTimeNow }) => ({
      scopeName: branchName,
      numCustomers,
      numQueued,
      maxQueueTimeNow,
    }),
  );

  const toggleExpandIndex = (index) => {
    setExpandedAll((prevState) => {
      const newState = [...prevState];
      newState[index] = newState[index] === 0 ? 1 : 0;
      return newState;
    });
  };


  return (
    <Box>
      {branchId.length > 0 ? (
        <Box>
          <RealtimeEnterpriseSummary
            title="Enterprise Queue Summary"
            kpis={[
              {
                label: 'Customers today',
                value: formatSummaryNumber(sumSummaryRows(enterpriseSummaryRows, 'numCustomers')),
              },
              {
                label: 'Customers in queue',
                value: formatSummaryNumber(sumSummaryRows(enterpriseSummaryRows, 'numQueued')),
              },
              {
                label: 'Online agents',
                value: `${numOnline}/${numAgents}`,
              },
            ]}
            tables={[
              {
                title: 'Queue highlights',
                columns: [
                  { id: 'scopeName', label: 'Branch' },
                  { id: 'numCustomers', label: 'Customers' },
                  { id: 'numQueued', label: 'In queue' },
                  { id: 'maxQueueTimeNow', label: 'Max queue' },
                ],
                rows: enterpriseSummaryRows,
                renderCell: (row, columnId) => {
                  if (columnId === 'scopeName') return row[columnId];
                  if (columnId === 'maxQueueTimeNow') {
                    return Number.isFinite(row[columnId]) ? formatTime(row[columnId]) : 'NA';
                  }
                  return formatSummaryNumber(row[columnId]);
                },
              },
            ]}
          />
          {branchesData?.map(
            (
              {
                customersData,
                numCustomers,
                branchId,
                numQueued,
                maxQueueTimeNow,
              },
              i,
            ) => {
              const joinedQueueThisHour = ticketsClone.filter(
                (a) => a.branchId === branchId
                  && dayjs.unix(a.created).format('h A') === dayjs().format('h A'),
              ).length;
              const stats = [
                {
                  title: 'Total Customers Today',
                  stat: numCustomers,
                  icon: IoPeopleOutline,
                  iconColor: 'var(--green)',
                },
                {
                  title: 'Customers in Queue',
                  stat: numQueued,
                  icon: IoPeopleOutline,
                  iconColor: 'var(--orange)',
                },
                {
                  title: 'Joined Queue This Hour',
                  stat: joinedQueueThisHour,
                  icon: IoTimeOutline,
                  iconColor: 'var(--green)',
                },
                {
                  title: 'Max. Queue Time Now',
                  stat: !Number.isNaN(maxQueueTimeNow) && Number.isFinite(maxQueueTimeNow) ? formatTime(maxQueueTimeNow) : 'NA',
                  statNumberId: `maxQueueTimeNow-${branchId}`,
                  icon: IoTimeOutline,
                  iconColor: 'var(--red)',
                },

                {
                  title: 'Customers Being Served Now',
                  stat: numActiveTickets,
                  icon: IoPeopleOutline,
                  iconColor: 'var(--green)',
                },
                {
                  title: 'Completed',
                  stat: numCompletedTickets,
                  icon: IoCheckmarkCircleOutline,
                  iconColor: 'var(--orange)',
                },
                {
                  title: 'No Show',
                  stat: numNoShowTickets,
                  icon: IoCloseCircleOutline,
                  iconColor: 'var(--green)',
                },
                {
                  title: 'Online Agents',
                  stat: `${numOnline}/${numAgents}`,
                  statNumberId: `maxQueueTimeNow-${branchId}`,
                  icon: IoIdCardOutline,
                  iconColor: 'var(--black-dry)',
                },
                
              ];

              return (
                <Box key={branchId}>
                  {numCustomers > 0 ? (
                    <Box mt={4}>
                      <SimpleGrid
                        columns={{ base: 1, md: 4 }}
                        gap={0}
                        mb={"10px"}
                      >
                        {
                      stats.map((a, i) => (
                        <Box
                          key={i}
                          px="20px"
                          py="15px"
                          mb="5px"
                          border="1px solid var(--borderGrey)"
                          borderRight={{
                            base: '1px solid #E5E4E5',
                            md: [3, 7].includes(i) ? '1px solid #E5E4E5' : 'none',
                          }}
                          borderRadius={{
                            base: '10px',
                            md: [0, 4].includes(i)
                              ? '10px 0 0 10px'
                              : [3, 7].includes(i)
                                ? '0 10px 10px 0'
                                : '0',
                          }}
                        >
                          <StatsCard title={a.title} stat={a.stat} statNumberId={a.statNumberId} icon={a.icon} iconColor={a.iconColor} />
                        </Box>
                      ))
                    }
                      </SimpleGrid>
                    </Box>
                  ) : (
                    <Box>
                      <Text>No customers for this branch</Text>
                    </Box>
                  )}

                  {/* View Switcher */}
                  <Box textAlign="center" mt="40px" mb="30px">
                    <Heading fontSize="22px" fontWeight={600} color="var(--black-dry)" mb="6px">
                      Activity View
                    </Heading>
                    <Text fontSize="14px" color="var(--gray-8)" mb="16px">
                      View real-time activity by customers, agents, or services.
                    </Text>
                    <HStack justify="center" gap="0" display="inline-flex" bg="#f5f5f5" borderRadius="full" p="4px">
                      {['Customers', 'Agents', 'Services'].map((tab) => (
                        <Box
                          key={tab}
                          as="button"
                          onClick={() => setActiveTab(tab.toLowerCase())}
                          px="24px"
                          py="8px"
                          fontSize="14px"
                          fontWeight={500}
                          borderRadius="full"
                          bg={activeTab === tab.toLowerCase() ? 'white' : 'transparent'}
                          color="var(--black-dry)"
                          boxShadow={activeTab === tab.toLowerCase() ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'}
                          cursor="pointer"
                          transition="all 0.2s"
                          _hover={{ bg: activeTab === tab.toLowerCase() ? 'white' : '#ebebeb' }}
                        >
                          {tab}
                        </Box>
                      ))}
                    </HStack>
                  </Box>

                  {/* Agents Tab */}
                  {activeTab === 'agents' ? (() => {
                    // Group active tickets by agent
                    const activeByAgent = {};
                    customersData
                      .filter((a) => a.ticket.status === TICKET_STATUS.ACTIVE)
                      .forEach((a) => {
                        const agentId = a.ticket?.manage?.userId || 'unassigned';
                        if (!activeByAgent[agentId]) activeByAgent[agentId] = { agentName: a.agentFullName, tickets: [] };
                        activeByAgent[agentId].tickets.push(a);
                      });

                    // Build idle agents list (in agentsData but not actively serving)
                    const activeAgentIds = Object.keys(activeByAgent);
                    const idleAgents = agentsData.filter((a) => !activeAgentIds.includes(a.agentId));

                    return (
                    <VStack
                      gap="20px"
                      maxW={{ base: '100%', md: '100%', lg: '70%', xl: '50%' }}
                      align="start"
                      mt="20px"
                      w="100%"
                    >
                      {/* Active Agents — serving customers */}
                      <Box w="100%">
                        <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)" mb="10px">
                          Active Agents ({Object.keys(activeByAgent).length})
                        </Heading>
                        {Object.keys(activeByAgent).length > 0 ? (
                          Object.entries(activeByAgent).map(([agentId, agentInfo]) => (
                            <Box key={agentId} mb="16px">
                              <HStack gap="10px" mb="8px">
                                <Text fontSize="16px" fontWeight={500} color="var(--black-dry)">
                                  {agentInfo.agentName}
                                </Text>
                                <Badge colorPalette="green" variant="subtle" fontSize="11px" px="8px" borderRadius="full">
                                  Serving
                                </Badge>
                              </HStack>
                              {agentInfo.tickets.map((a) => (
                                <CustomerCard key={a.ticket.id} {...a} expandedAll={0} />
                              ))}
                            </Box>
                          ))
                        ) : (
                          <Text fontSize="sm" color="var(--gray-8)">No agents currently serving</Text>
                        )}
                      </Box>

                      {/* Idle Agents — logged in but not serving */}
                      <Box w="100%">
                        <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)" mb="10px">
                          Idle Agents ({idleAgents.length})
                        </Heading>
                        {idleAgents.length > 0 ? (
                          idleAgents.map((agent) => (
                            <Box
                              key={agent.agentId}
                              w="100%"
                              border="1px solid var(--borderGrey)"
                              borderRadius="10px"
                              overflow="hidden"
                              mb="8px"
                            >
                              <HStack justify="space-between" px="16px" py="12px">
                                <HStack gap="10px">
                                  <Text fontSize="16px" fontWeight={500} color="var(--black-dry)">
                                    {agent.agentName}
                                  </Text>
                                  <Badge colorPalette="orange" variant="subtle" fontSize="11px" px="8px" borderRadius="full">
                                    Idle
                                  </Badge>
                                </HStack>
                              </HStack>
                              <HStack
                                justify="space-between"
                                px="16px"
                                py="10px"
                                bg="#fafafa"
                                borderTop="1px solid var(--borderGrey)"
                              >
                                <VStack gap="0" align="start">
                                  <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Total Today</Text>
                                  <Text fontSize="14px" fontWeight={500}>{agent.total || 0}</Text>
                                </VStack>
                                <VStack gap="0" align="start">
                                  <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Completed</Text>
                                  <Text fontSize="14px" fontWeight={500} color="var(--green)">{agent.completed || 0}</Text>
                                </VStack>
                                <VStack gap="0" align="start">
                                  <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Avg Time</Text>
                                  <Text fontSize="14px" fontWeight={500}>{agent.averageTime || 'NA'}</Text>
                                </VStack>
                              </HStack>
                            </Box>
                          ))
                        ) : (
                          <Text fontSize="sm" color="var(--gray-8)">No idle agents</Text>
                        )}
                      </Box>
                    </VStack>
                    );
                  })() : null}

                  {/* Services Tab */}
                  {activeTab === 'services' ? (() => {
                    // For each service, find agents actively serving and agents assigned but idle
                    const servicesWithAgents = servicesData?.map((svc) => {
                      // Active agents: currently serving a ticket for this service
                      const activeTickets = customersData.filter(
                        (a) => a.ticket.serviceId === svc.serviceId && a.ticket.status === TICKET_STATUS.ACTIVE
                      );
                      const activeAgentIds = [...new Set(activeTickets.map((a) => a.ticket?.manage?.userId).filter(Boolean))];
                      const activeAgentNames = activeAgentIds.map((id) => {
                        const agent = agents.find((a) => a.id === id);
                        return agent ? `${agent.name} ${agent.lastname}` : 'Unknown';
                      });

                      // Assigned agents: users whose services array includes this service
                      const assignedAgents = agents.filter(
                        (a) => a.services?.includes(svc.serviceId) && a.branchId === branchId
                      );
                      const totalAssigned = assignedAgents.length;

                      // Idle agents for this service: assigned but not actively serving this service
                      const idleAgentNames = assignedAgents
                        .filter((a) => !activeAgentIds.includes(a.id))
                        .map((a) => `${a.name} ${a.lastname}`);

                      return { ...svc, activeAgentNames, idleAgentNames, activeCount: activeAgentIds.length, totalAssigned };
                    }) || [];

                    return (
                    <VStack
                      gap="12px"
                      maxW={{ base: '100%', md: '100%', lg: '70%', xl: '50%' }}
                      align="start"
                      mt="20px"
                    >
                      <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)" mb="4px">
                        Services ({servicesWithAgents.length})
                      </Heading>
                      {servicesWithAgents.length > 0 ? (
                        servicesWithAgents.map((svc) => (
                          <Box
                            key={svc.serviceId}
                            w="100%"
                            border="1px solid var(--borderGrey)"
                            borderRadius="10px"
                            overflow="hidden"
                          >
                            <HStack justify="space-between" px="16px" py="12px">
                              <Text fontSize="16px" fontWeight={500} color="var(--black-dry)">
                                {svc.serviceName}
                              </Text>
                              <Badge
                                colorPalette={svc.activeCount > 0 ? 'green' : 'gray'}
                                variant="subtle"
                                fontSize="11px"
                                px="8px"
                                borderRadius="full"
                              >
                                Agents {svc.activeCount}/{svc.totalAssigned}
                              </Badge>
                            </HStack>
                            <HStack
                              justify="space-between"
                              px="16px"
                              py="10px"
                              bg="#fafafa"
                              borderTop="1px solid var(--borderGrey)"
                            >
                              <VStack gap="0" align="start">
                                <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Total</Text>
                                <Text fontSize="14px" fontWeight={500}>{svc.total || 0}</Text>
                              </VStack>
                              <VStack gap="0" align="start">
                                <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Completed</Text>
                                <Text fontSize="14px" fontWeight={500} color="var(--green)">{svc.completed || 0}</Text>
                              </VStack>
                              <VStack gap="0" align="start">
                                <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Active</Text>
                                <Text fontSize="14px" fontWeight={500} color="var(--blue-classic)">{svc.active || 0}</Text>
                              </VStack>
                              <VStack gap="0" align="start">
                                <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">In Queue</Text>
                                <Text fontSize="14px" fontWeight={500} color="var(--orange)">{svc.inQueue || 0}</Text>
                              </VStack>
                            </HStack>
                            {/* Agent breakdown */}
                            {(svc.activeAgentNames.length > 0 || svc.idleAgentNames.length > 0) ? (
                              <Box px="16px" py="8px" borderTop="1px solid var(--borderGrey)">
                                {svc.activeAgentNames.length > 0 ? (
                                  <HStack gap="6px" mb="4px" flexWrap="wrap">
                                    <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Active:</Text>
                                    {svc.activeAgentNames.map((name, i) => (
                                      <Badge key={i} colorPalette="green" variant="subtle" fontSize="11px" borderRadius="full">{name}</Badge>
                                    ))}
                                  </HStack>
                                ) : null}
                                {svc.idleAgentNames.length > 0 ? (
                                  <HStack gap="6px" flexWrap="wrap">
                                    <Text fontSize="11px" color="var(--gray-8)" textTransform="uppercase">Idle:</Text>
                                    {svc.idleAgentNames.map((name, i) => (
                                      <Badge key={i} colorPalette="gray" variant="subtle" fontSize="11px" borderRadius="full">{name}</Badge>
                                    ))}
                                  </HStack>
                                ) : null}
                              </Box>
                            ) : null}
                          </Box>
                        ))
                      ) : (
                        <Text fontSize="sm" color="var(--gray-8)">No service data available</Text>
                      )}
                    </VStack>
                    );
                  })() : null}

                  {/* Customers Tab */}
                  {activeTab === 'customers' ? (<VStack
                    gap="50px"
                    maxW={selectedTicketsStatus === ''
                      ? { base: '100%', md: '100%', lg: '100%', xl: '100%' }
                      : { base: '100%', md: '100%', lg: '70%', xl: '50%' }
                    }
                    align="start"
                    mt="40px"
                  >
                  {/* Side by side: Active + In Queue (only when showing all tickets) */}
                  {selectedTicketsStatus === '' ? (
                    <SimpleGrid columns={{ base: 1, lg: 2 }} gap="30px" w="100%">
                      <Box w="100%">
                        <HStack justifyContent="space-between" mb="8px">
                          <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)">
                            Being Served ({customersData?.filter((a) => a.ticket.status === 1).length || 0})
                          </Heading>
                          <Text _hover={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => toggleExpandIndex(1)}>
                            {customersData?.filter((a) => a.ticket.status === 1).length || 0
                              ? expandedAll[1] ? 'Collapse All' : 'Expand All' : ''}
                          </Text>
                        </HStack>
                        {customersData?.filter((a) => a.ticket.status === 1)
                          .map((a) => <CustomerCard key={a.ticket.id} {...a} expandedAll={expandedAll[1]} />)}
                      </Box>
                      <Box w="100%">
                        <HStack justifyContent="space-between" mb="8px">
                          <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)">
                            In Queue ({customersData?.filter((a) => a.ticket.status === 0).length || 0})
                          </Heading>
                          <Text _hover={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => toggleExpandIndex(0)}>
                            {customersData?.filter((a) => a.ticket.status === 0).length || 0
                              ? expandedAll[0] ? 'Collapse All' : 'Expand All' : ''}
                          </Text>
                        </HStack>
                        {customersData?.filter((a) => a.ticket.status === 0)
                          .map((a) => <CustomerCard key={a.ticket.id} {...a} expandedAll={expandedAll[0]} />)}
                      </Box>
                    </SimpleGrid>
                  ) : null}
                  {/* Single column when filtered to specific status */}
                  {selectedTicketsStatus !== '' && Number(selectedTicketsStatus) === 1 ? (
                  <Box w="100%" maxW={{ lg: '70%', xl: '50%' }}>
                    <HStack justifyContent="space-between" mb="8px">
                      <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)">
                        Customers Being Served Now ({customersData?.filter((a) => a.ticket.status === 1).length || 0})
                      </Heading>
                      <Text _hover={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => toggleExpandIndex(1)}>
                        {customersData?.filter((a) => a.ticket.status === 1).length || 0
                          ? expandedAll[1] ? 'Collapse All' : 'Expand All' : ''}
                      </Text>
                    </HStack>
                    {customersData?.filter((a) => a.ticket.status === 1)
                      .map((a) => <CustomerCard key={a.ticket.id} {...a} expandedAll={expandedAll[1]} />)}
                  </Box>
                  ) : null}
                  {selectedTicketsStatus !== '' && Number(selectedTicketsStatus) === 0 ? (
                  <Box w="100%" maxW={{ lg: '70%', xl: '50%' }}>
                    <HStack justifyContent="space-between" mb="8px">
                      <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)">
                        Customers in Queue ({customersData?.filter((a) => a.ticket.status === 0).length || 0})
                      </Heading>
                      <Text _hover={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => toggleExpandIndex(0)}>
                        {customersData?.filter((a) => a.ticket.status === 0).length || 0
                          ? expandedAll[0] ? 'Collapse All' : 'Expand All' : ''}
                      </Text>
                    </HStack>
                    {customersData?.filter((a) => a.ticket.status === 0)
                      .map((a) => <CustomerCard key={a.ticket.id} {...a} expandedAll={expandedAll[0]} />)}
                  </Box>
                  ) : null}
                {/* No shows */}
                {
                Number(selectedTicketsStatus) === 2 ? (
                <Box w="100%">
                  <HStack
                    justifyContent="space-between"
                  >
                    <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)">
                      No-show Customers (
                      {customersData?.filter((a) => a.ticket.status === 2).length || 0}
                      )
                    </Heading>
                    <Text
                      _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => toggleExpandIndex(2)}
                    >
                      {
                        customersData?.filter((a) => a.ticket.status === 2).length || 0
                          ? expandedAll[2] ? 'Collapse All' : 'Expand All'
                          : ''
                      }
                    </Text>
                  </HStack>
                  {
                    customersData?.filter((a) => a.ticket.status === 2)
                      .map((a) => <CustomerCard key={a.ticket.id} {...a} expandedAll={expandedAll[2]} />)
                  }
                </Box>
                ) : null
                }
                {/* completed tickets */}
                {
                  Number(selectedTicketsStatus) === 3 ? (
                  <Box w="100%">
                    <HStack
                      justifyContent="space-between"
                    >
                      <Heading fontSize="22px" fontWeight={400} color="var(--gray-45)">
                        Completed Tickets (
                        {customersData?.filter((a) => a.ticket.status === 3).length || 0}
                        )
                      </Heading>
                      <Text
                        _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={() => toggleExpandIndex(3)}
                      >
                        {
                        customersData?.filter((a) => a.ticket.status === 3).length || 0
                          ? expandedAll[3] ? 'Collapse All' : 'Expand All'
                          : ''
                        }
                      </Text>
                    </HStack>
                    {
                    customersData?.filter((a) => a.ticket.status === 3)
                      .map((a) => <CustomerCard key={a.ticket.id} {...a} expandedAll={expandedAll[3]} />)
                  }
                  </Box>
                  ) : null
                }
                  </VStack>) : null}

                </Box>
              );
            },
          )}
        </Box>
      ) : (
        <SelectBranchInfo />
      )}
    </Box>
  );
}

export default CustomersInQueue;
